import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TFunction } from 'i18next';
import isEqual from 'lodash/isEqual';

import { useDebounce } from 'hooks/useDebounce';
import { useNewFilters } from 'hooks/useFilters';
import { createHandle } from 'services/breadcrumbs/breadcrumbs';
import { ConnectionsFilterKey, useInfiniteConnectionsV2 } from 'services/repositories/connections/connections';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useConnectionsStats } from 'services/repositories/stats/stats';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { formatDayAndTime } from 'support/helpers/dateTime/dateTime';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { ConnectionPartner, ConnectionsRoutes } from 'support/types';
import { ConnectionDetailsLocationState } from 'pages/Connections/ConnectionDetailsPage/ConnectionDetailsPage';
import { convertToPartnerV2ToRequiredPartnerDTO } from 'pages/TradePartners/NewTradeRequest/NewTradeRequest';
import { ConnectionsActionRequiredBadge } from 'components/ConnectionsActionRequiredBadge/ConnectionsActionRequiredBadge';
import { Alert } from 'components/Display/Alert/Alert';
import { Filters } from 'components/Filters/Filters';
import { InfiniteScroll } from 'components/ListTable/InfiniteScroll/InfiniteScroll';
import { ListTable } from 'components/ListTable/ListTable';
import { SortState, TableHeader, TableItem } from 'components/ListTable/types';
import { Page } from 'components/Page/Page';
import { TradePartnerNameAndLogo, TradePartnerNameAndLogoClamping } from 'components/TradePartnerNameAndLogo';

import { DocumentTypeBadges } from './components/DocumentTypeBadges/DocumentTypeBadges';
import { getLatestTradeRequestDate } from './helpers/getLatestTradeRequestDate';
import { areSameFilterValue, getDefaultFilterConfig } from './tableConfig';

const ACTION_REQUIRED_FILTER_VALUE: string = ['READY_FOR_TESTING', 'HAS_TEST_MESSAGES;IN_REVIEW', 'TASKS_PENDING'].join(
  ',',
);

const SORT_KEY_TO_API_FIELD: Record<string, string> = {
  'trade-partner': 'name',
  'latest-request-date': 'latest_request_date',
};

export function Connections() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const defaultConfig = useMemo(() => getDefaultFilterConfig(), []);
  const { filterValues, filters, perPage, updateFilters, clearAllFilters } = useNewFilters(
    defaultConfig,
    'connections',
  );
  const hasFilters = !areSameFilterValue(filterValues, defaultConfig);
  const debouncedFilterValues = useDebounce(filterValues, 150);
  const differentFilters = !isEqual(debouncedFilterValues, filterValues);
  const [sortState, setSortState] = useState<SortState>({ key: 'trade-partner', direction: 'asc' });

  const {
    data: relationshipsData,
    isLoading: relationshipsLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasPreviousPage,
    hasNextPage,
  } = useInfiniteConnectionsV2({
    variables: {
      query: {
        filterValues: debouncedFilterValues,
        perPage,
        sort: sortStateToApiParam(sortState),
      },
    },
  });

  const { data: currentPartner, isLoading: currentPartnerLoading } = useCurrentPartner();

  const actionRequiredFilterValue = ACTION_REQUIRED_FILTER_VALUE;
  const { data: actionRequiredStatsData } = useConnectionsStats({
    variables: {
      query: {
        filterValues: [
          ...debouncedFilterValues.filter(({ key }) => key !== ConnectionsFilterKey.actionRequired),
          {
            key: ConnectionsFilterKey.actionRequired,
            value: actionRequiredFilterValue,
          },
        ],
      },
    },
  });

  const showActionRequiredColumn = (actionRequiredStatsData?.connectionsCount ?? 0) > 0;

  const tableHeaders: Array<TableHeader> = useMemo(() => {
    const headers: Array<TableHeader> = [
      { label: t('connections:table.fields.tradePartner'), key: 'trade-partner', sortable: true },
      { label: t('connections:table.fields.documentTypes'), key: 'document-types' },
      { label: t('connections:table.fields.latestRequestDate'), key: 'latest-request-date', sortable: true },
    ];

    if (showActionRequiredColumn) {
      headers.push({ label: '', key: 'action-required' });
    }

    return headers;
  }, [t, showActionRequiredColumn]);

  const buildRow = (partner: ConnectionPartner): Array<TableItem> => {
    const latestDate = getLatestTradeRequestDate(partner);
    const items: Array<TableItem> = [
      {
        element: <TradePartnerNameAndLogo partner={partner} clamping={TradePartnerNameAndLogoClamping.HORIZONTAL} />,
        key: `tp-name-${partner.id}`,
      },
      {
        element: <DocumentTypeBadges currentPartnerId={currentPartner?.id || ''} otherPartner={partner} />,
        key: `documentType-${partner.id}`,
      },
      {
        element: latestDate ? formatDayAndTime(latestDate) : EMPTY_FIELD,
        key: `latestRequestDate-${partner.id}`,
      },
    ];

    if (showActionRequiredColumn) {
      items.push({
        key: `actionRequired-${partner.id}`,
        element: (
          <ConnectionsActionRequiredBadge
            relationships={[...(partner.receiver_relationships || []), ...(partner.sender_relationships || [])]}
            currentPartnerId={currentPartner?.id || ''}
          />
        ),
      });
    }

    return items;
  };

  const data = relationshipsData?.pages?.flatMap((page) => page.data);

  const emptyData = data?.length === 0;

  return (
    <Page>
      <Page.Head title={t('common:mainMenu.connections')} description={t('connections:description')}>
        <Filters
          propertyFilters={filters}
          onFiltersChange={updateFilters}
          filterValues={filterValues}
          clearAllFilters={clearAllFilters}
        />
      </Page.Head>

      <Page.Section>
        <ActionsRequiredBanner />
        <ListTable<ConnectionPartner>
          headers={tableHeaders}
          data={data}
          isLoading={relationshipsLoading || currentPartnerLoading || (differentFilters && emptyData)}
          rowBuilder={buildRow}
          sortState={sortState}
          onSortChange={setSortState}
          onRowClick={(partner) =>
            navigate(routeToPage(ConnectionsRoutes.connectionDetails, { partnerId: partner.id }), {
              state: {
                partner: convertToPartnerV2ToRequiredPartnerDTO(partner),
              } satisfies ConnectionDetailsLocationState,
            })
          }
          pagination={
            <InfiniteScroll
              hasMore={Boolean(hasNextPage)}
              loadMore={fetchNextPage}
              loading={isFetchingNextPage}
              noMoreItemsEnabled={Boolean(hasPreviousPage)}
            />
          }
          emptyState={
            <ListTable.EmptyState
              background="gray"
              cols={tableHeaders.length}
              cta={
                hasFilters
                  ? {
                      children: t('common:table.withFilters.emptyState.cta'),
                      onClick: clearAllFilters,
                      analyticsId: 'connections:empty_state_cta',
                    }
                  : undefined
              }
            >
              {hasFilters ? t('common:table.withFilters.emptyState.title') : t('connections:list.emptyState')}
            </ListTable.EmptyState>
          }
        />
      </Page.Section>
    </Page>
  );
}

const sortStateToApiParam = (sortState: SortState): string | null => {
  if (!sortState) {
    return null;
  }

  const apiField = SORT_KEY_TO_API_FIELD[sortState.key];
  if (!apiField) {
    return null;
  }

  return sortState.direction === 'desc' ? `-${apiField}` : apiField;
};

const ActionsRequiredBanner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const actionRequiredFilterValue = ACTION_REQUIRED_FILTER_VALUE;
  const { data: connectionsCountData } = useConnectionsStats({
    variables: {
      query: {
        filterValues: [
          {
            key: ConnectionsFilterKey.actionRequired,
            value: actionRequiredFilterValue,
          },
          {
            key: ConnectionsFilterKey.deactivated_partners,
            value: 'false',
          },
        ],
      },
    },
    refetchOnMount: 'always',
  });

  const actionRequiredRelationshipsCount = connectionsCountData?.connectionsCount ?? 0;
  if (!actionRequiredRelationshipsCount) {
    return null;
  }

  return (
    <Alert
      className="mb-4"
      severity="info"
      withoutIcon
      title={t('connections:list.alert.hasActionRequiredRelationships.title', {
        count: actionRequiredRelationshipsCount,
      })}
      actions={{
        alignment: 'right',
        primary: {
          variant: 'secondary',
          children: t('connections:list.alert.hasActionRequiredRelationships.action'),
          onClick: () =>
            navigate(
              routeToPage(ConnectionsRoutes.root, undefined, {
                [ConnectionsFilterKey.actionRequired]: actionRequiredFilterValue,
                [ConnectionsFilterKey.deactivated_partners]: 'false',
              }),
            ),
          analyticsId: 'connections:action_required_banner_cta',
        },
      }}
    >
      {t('connections:list.alert.hasActionRequiredRelationships.description')}
    </Alert>
  );
};

Connections.handle = createHandle(() => ({
  label: (t: TFunction) => t('common:mainMenu.connections'),
  to: ConnectionsRoutes.root,
  key: 'connections',
}));
